import { useEffect } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import {
  SECTIONS,
  healthOf,
  isSectionUnavailable,
  touchRecent,
  type SectionKey,
} from "../admin/health";
import {
  AdAvatar,
  Callout,
  HealthDot,
  Pill,
  ToastProvider,
  errText,
  monthYear,
  useToast,
} from "../admin/ui";
import { features } from "../config/features";
import { FormsSection } from "../admin/customer/FormsSection";
import { OverviewSection } from "../admin/customer/OverviewSection";
import { ReportingSection } from "../admin/customer/ReportingSection";
import { SsoSection } from "../admin/customer/SsoSection";
import { SyncSection } from "../admin/customer/SyncSection";
import { UsersSection } from "../admin/customer/UsersSection";
import { api, type AdminClientDetail, type Me } from "../services/api";

/**
 * One customer, everything in one place — /admin/clients/:id/:section
 *
 * A sticky left sub-nav with a setup-health dot per section, and one section
 * in view at a time. Each section owns its own save scope: there is no
 * pane-wide Save, so "what am I about to persist" is always the card you are
 * looking at. The section is in the URL, so back works and links are shareable.
 */
export function AdminCustomer() {
  const { id, section } = useParams<{ id: string; section?: string }>();
  const { data: me } = useSWR<Me>("/api/me", () => api.me());

  if (!id) return <Navigate to="/admin/clients" replace />;
  if (!me) return <div className="card">Loading…</div>;
  if (me.role !== "iquest_admin") {
    return (
      <div className="card empty-state">
        <p>You don&apos;t have access to this page.</p>
        <p className="empty-state-blurb">
          Configuring a customer is reserved for iQuest admins.
        </p>
      </div>
    );
  }
  // Validate the section only once `me` has landed: Reporting's visibility
  // depends on it, and guarding earlier bounced every direct /reporting link
  // to Overview while the identity was still in flight.
  const reportingOn = features.reporting && me.reporting_enabled === true;
  if (
    section &&
    !SECTIONS.some((s) => s.key === section) &&
    !(section === "reporting" && reportingOn)
  ) {
    return <Navigate to={`/admin/clients/${id}`} replace />;
  }

  return (
    <ToastProvider>
      <CustomerInner
        companyId={id}
        section={(section as SectionKey | undefined) ?? "overview"}
        me={me}
      />
    </ToastProvider>
  );
}

function CustomerInner({
  companyId,
  section,
  me,
}: {
  companyId: string;
  section: SectionKey;
  me: Me;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const isNew = params.get("new") === "1";
  const setupProblems = (useLocation().state as { setupProblems?: string[] } | null)
    ?.setupProblems;

  const { data, mutate, error } = useSWR<AdminClientDetail>(
    ["/api/admin/clients", companyId, "health"],
    () => api.admin.clients.get(companyId, { include: "health" }),
  );

  useEffect(() => {
    touchRecent(me.id, companyId);
  }, [me.id, companyId]);

  if (error) {
    return (
      <div className="card form-error">
        Failed to load customer: {String((error as Error).message ?? error)}
      </div>
    );
  }
  if (!data) return <div className="card">Loading…</div>;

  const health = healthOf(data);

  async function togglePortal() {
    if (!data) return;
    const next = !data.is_active;
    if (
      !next &&
      !window.confirm(
        `Disable the portal for ${data.name}? Nobody there will be able to sign in. Their data is kept.`,
      )
    ) {
      return;
    }
    try {
      await api.admin.clients.update(companyId, { is_active: next });
      await mutate();
      toast(next ? "Portal enabled" : "Portal disabled", next ? "ok" : "warn");
    } catch (e) {
      toast(errText(e, "Could not change the portal state"), "warn");
    }
  }

  function exportAudit() {
    api.auditLog
      .exportCsv(companyId)
      .then(() => toast("Audit log CSV downloading"))
      .catch((e: unknown) => toast(errText(e, "Audit log export failed"), "warn"));
  }

  const refreshHealth = () => void mutate();

  return (
    <div className="rf-wrap">
      <Link className="ad-back" to="/admin/clients">
        <Icon name="arrowLeft" /> Customers
      </Link>

      <div className="ad-head">
        <div>
          <div className="ad-head-title">
            <AdAvatar name={data.name} size="lg" />
            <div>
              <h1 className="rf-h1">{data.name}</h1>
              <div
                className="mono"
                style={{
                  marginTop: 4,
                  fontFamily: "var(--iq-font-mono)",
                  fontSize: 12,
                  color: "var(--iq-text-muted)",
                }}
              >
                {data.autotask_company_id != null
                  ? `AT ${data.autotask_company_id}`
                  : "Autotask not linked"}{" "}
                · {data.allowed_email_domains[0] ?? "no domain"} · {data.user_count} user
                {data.user_count === 1 ? "" : "s"} · since {monthYear(data.created_at)}
              </div>
            </div>
          </div>
        </div>
        <div className="grp">
          {data.is_active ? (
            <Pill kind="ok">Portal active</Pill>
          ) : (
            <Pill kind="off">Portal disabled</Pill>
          )}
          <button
            type="button"
            className={`btn btn-sm${data.is_active ? " btn-danger" : " btn-ghost"}`}
            onClick={() => void togglePortal()}
          >
            {data.is_active ? "Disable portal" : "Enable portal"}
          </button>
        </div>
      </div>

      {isNew && (
        <div style={{ marginBottom: 20 }}>
          <Callout kind="ok">
            <strong>{data.name} is on the portal.</strong>{" "}
            {data.health_error
              ? // The warning callout below already says what could not be read.
                "Anything still open is marked on the left."
              : !data.setup_health
              ? "Setup status could not be read."
              : (data.setup_health.unavailable ?? []).length > 0
                ? "Some setup checks could not be read. Anything still open is marked on the left."
                : health.done < health.total
                  ? "Anything you skipped is marked in the list on the left. Finish it whenever suits."
                  : "Everything is set up."}
          </Callout>
        </div>
      )}

      {setupProblems && setupProblems.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Callout kind="warn">
            <strong>The customer was created, but some setup didn&apos;t apply.</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {setupProblems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Callout>
        </div>
      )}

      {data.health_error && (
        <div style={{ marginBottom: 20 }}>
          <Callout kind="warn">
            <strong>Some setup checks could not be read.</strong> {data.health_error}
          </Callout>
        </div>
      )}

      <div className="ad-cust">
        <nav className="ad-cnav">
          <div className="lbl">Configure</div>
          {SECTIONS.map((s) => (
            <Link
              key={s.key}
              className={s.key === section ? "active" : ""}
              to={`/admin/clients/${companyId}/${s.key}`}
            >
              {s.label}
              <HealthDot
                state={
                  data.setup_health && !isSectionUnavailable(health, s.key)
                    ? health[s.key]
                    : null
                }
              />
            </Link>
          ))}
          {features.reporting && me.reporting_enabled && (
            <Link
              className={section === "reporting" ? "active" : ""}
              to={`/admin/clients/${companyId}/reporting`}
            >
              Reporting
              <HealthDot
                state={
                  data.setup_health && !isSectionUnavailable(health, "reporting")
                    ? health.reporting
                    : null
                }
              />
            </Link>
          )}
          <div className="lbl">Tools</div>
          <Link to={`/admin/view-as?c=${companyId}`}>
            <Icon name="eye" size={15} /> View portal as…
          </Link>
          <button type="button" className="navbtn" onClick={exportAudit}>
            <Icon name="list" size={15} /> Export audit log
          </button>
          <div className="legend">
            <span>
              <HealthDot state="ok" />
              Complete
            </span>
            <span>
              <HealthDot state="warn" />
              Needs attention
            </span>
            <span>
              <HealthDot state="todo" />
              Not set up
            </span>
          </div>
        </nav>

        <div className="ad-cbody">
          {section === "overview" && (
            <OverviewSection
              client={data}
              onSaved={async () => {
                await mutate();
              }}
              onGoTo={(key) => navigate(`/admin/clients/${companyId}/${key}`)}
            />
          )}
          {section === "users" && (
            <UsersSection client={data} me={me} onChanged={refreshHealth} />
          )}
          {section === "sso" && (
            <SsoSection
              client={data}
              onSaved={async () => {
                await mutate();
              }}
            />
          )}
          {section === "forms" && (
            <FormsSection client={data} onChanged={refreshHealth} />
          )}
          {section === "sync" && <SyncSection client={data} onChanged={refreshHealth} />}
          {section === "reporting" && features.reporting && me.reporting_enabled && (
            <ReportingSection client={data} me={me} />
          )}
        </div>
      </div>
    </div>
  );
}
